/**
 * Concept Units under a Concept — shared by the Reference > Design > Concepts branch
 * (DesignSection.jsx) and the DAPC Drawings milestone Design node
 * (MilestoneSections.jsx). Self-contained: fetches/mutates its own unit list via
 * services/design.js and mirrors every change into ctx.unitCache so the vanilla
 * outline engine's breadcrumb/label-resolution code stays correct. See
 * ConceptLevelsSection.jsx for the level-of-nesting below this one.
 *
 * Renders in two modes — tree (default, full header) vs zoomMode (children only,
 * used for sec:concept:<conceptId>:units; "Add Unit" reaches this component via the
 * 'open-add-unit' CustomEvent, detail { conceptId }).
 */
import { useState, useEffect, useRef } from 'react'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider } from './ActionMenu.jsx'
import { confirmTypeDialog } from './Dialog.jsx'
import { UnitTypeModal } from './ConceptUnitModals.jsx'
import ConceptLevelsSection from './ConceptLevelsSection.jsx'
import {
  getConceptUnits, createConceptUnit, updateConceptUnit,
  deleteConceptUnitCascade, getConceptLevels, getLevelRooms,
} from '../services/design.js'

function emptyRow(message) {
  return (
    <div className="wf-node wf-node--item">
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ opacity: 0 }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ color: 'rgba(7,39,45,0.4)', fontStyle: 'italic' }}>{message}</span>
        </div>
      </div>
    </div>
  )
}

export function ConceptUnitRow({ unit, conceptId, onRename, onDelete, hideAreaMetrics }) {
  const colKey = 'unit:' + unit.id
  const isCollapsed = wf.collapsed[colKey] !== false
  const unitName = unit.fields['Unit Type'] || unit.fields['Name'] || 'Unit'
  const menu = useActionMenu()
  const [editOpen, setEditOpen] = useState(false)

  async function handleDelete() {
    menu.close()
    const levels = await getConceptLevels(unit.id)
    const roomCounts = await Promise.all(levels.map(l => getLevelRooms(l.id)))
    const roomCount = roomCounts.reduce((sum, r) => sum + r.length, 0)
    const ok = await confirmTypeDialog({
      title: 'Delete unit?',
      message: `This will permanently delete "${unitName}", ${levels.length} level(s), and ${roomCount} room(s) under it. This cannot be undone.`,
    })
    if (!ok) return
    await onDelete(unit.id)
  }

  return (
    <div className={`wf-node wf-node--item${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={colKey}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={colKey}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--item" title="Focus on unit"
          data-fn="zoomTo" data-level="unit-detail" data-unitid={unit.id} data-conceptid={conceptId} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{unitName}</span>
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); setEditOpen(true) }}>Edit type</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={handleDelete} danger>Delete unit</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && (
        <div className="wf-children">
          <ConceptLevelsSection unitId={unit.id} conceptId={conceptId} hideAreaMetrics={hideAreaMetrics} />
        </div>
      )}
      {editOpen && (
        <UnitTypeModal
          title="Edit Unit Type"
          initial={unit.fields['Unit Type']}
          confirmLabel="Save"
          onSave={unitType => onRename(unit.id, unitType)}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}

export default function ConceptUnitsSection({ conceptId, zoomMode, hideAreaMetrics }) {
  const [units, setUnits] = useState(null)
  const loadedRef = useRef(false)
  const menu = useActionMenu()
  const [addOpen, setAddOpen] = useState(false)
  const key = 'sec:concept:' + conceptId + ':units'
  const isCollapsed = zoomMode ? false : (wf.collapsed[key] !== false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!conceptId || loadedRef.current) return
    loadedRef.current = true
    getConceptUnits(conceptId)
      .then(records => { const r = records || []; setUnits(r); ctx.unitCache[conceptId] = r; ctx.render() })
      .catch(() => setUnits([]))
  }, [conceptId])

  // zoomMode: "Add Unit" lives in ZoomTitle's ⋯ menu — bridged via CustomEvent.
  useEffect(() => {
    if (!zoomMode) return
    function onOpenAdd(e) { if (e.detail?.conceptId === conceptId) setAddOpen(true) }
    window.addEventListener('open-add-unit', onOpenAdd)
    return () => window.removeEventListener('open-add-unit', onOpenAdd)
  }, [zoomMode, conceptId])

  async function handleAdd(unitType) {
    await createConceptUnit(conceptId, unitType)
    const records = await getConceptUnits(conceptId)
    setUnits(records)
    ctx.unitCache[conceptId] = records
    ctx.render()
  }

  async function handleRename(unitId, unitType) {
    await updateConceptUnit(unitId, { 'Unit Type': unitType })
    setUnits(prev => {
      const next = (prev || []).map(u => u.id === unitId ? { ...u, fields: { ...u.fields, 'Unit Type': unitType } } : u)
      ctx.unitCache[conceptId] = next
      return next
    })
    ctx.render()
  }

  async function handleDelete(unitId) {
    await deleteConceptUnitCascade(unitId)
    setUnits(prev => {
      const next = (prev || []).filter(u => u.id !== unitId)
      ctx.unitCache[conceptId] = next
      return next
    })
    if (ctx.levelCache) delete ctx.levelCache[unitId]
    ctx.render()
  }

  const count = Array.isArray(units) ? units.length : null

  const content = (
    <>
      {!units && <div className="wf-loading">Loading…</div>}
      {Array.isArray(units) && units.length === 0 && emptyRow('No units yet')}
      {Array.isArray(units) && units.map(unit => (
        <ConceptUnitRow key={unit.id} unit={unit} conceptId={conceptId} onRename={handleRename} onDelete={handleDelete} hideAreaMetrics={hideAreaMetrics} />
      ))}
    </>
  )

  const addModal = addOpen && (
    <UnitTypeModal
      title="Add Unit"
      initial=""
      confirmLabel="Add"
      onSave={handleAdd}
      onClose={() => setAddOpen(false)}
    />
  )

  if (zoomMode) {
    return (
      <div className="wf-list">
        {content}
        {addModal}
      </div>
    )
  }

  return (
    <div className={`wf-node wf-node--cat${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--cat" title="Focus on Units"
          data-fn="zoomTo" data-level="concept-units" data-conceptid={conceptId} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">Units</span>
          {count != null && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 8 }}>{count}</span>}
        </div>
        <ActionMenuButton menu={menu} />
      </div>
      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); setAddOpen(true) }}>Add Unit</ActionMenuItem>
      </ActionMenu>
      {!isCollapsed && <div className="wf-children">{content}</div>}
      {addModal}
    </div>
  )
}
